import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import config from '../../config';

export default function Wallet() {
    const [wallets, setWallets] = useState([]);
    const [filteredWallets, setFilteredWallets] = useState([]);
    const [selectedWallets, setSelectedWallets] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState({ uid: '', status: '', amount: '' });
    const [isBulkAction, setIsBulkAction] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const { API_URL } = config;

    useEffect(() => {
        fetch(`${API_URL}/api/wallet/wallets`)
            .then(res => res.json())
            .then(data => {
                setWallets(data.wallets);
                setFilteredWallets(data.wallets);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching wallets:', error);
                setLoading(false);
            });
    }, [refresh]);

    useEffect(() => {
        if (searchTerm === "") {
            setFilteredWallets(wallets);
        } else {
            const filtered = wallets.filter(wallet => {
                const searchFields = [
                    wallet.username?.toLowerCase() || '',
                    wallet.type?.toLowerCase() || '',
                    wallet.amount?.toString().toLowerCase() || '',
                    wallet.phoneNumber?.toString().toLowerCase() || ''
                ];
                return searchFields.some(field => field.includes(searchTerm.toLowerCase()));
            });
            setFilteredWallets(filtered);
        }
    }, [searchTerm, wallets]);

    const filterWallets = (type) => {
        if (type === 'all') {
            setFilteredWallets(wallets);
        } else {
            const filtered = wallets.filter(wallet => wallet.type === type);
            setFilteredWallets(filtered);
        }
    };

    const openModal = (uid, status, bulk = false) => {
        setIsBulkAction(bulk);
        const adjustedStatus = status === 'credited' ? 'added' : 'deducted';
        if (bulk) {
            setModalData({ uid: '', status: adjustedStatus, amount: '' });
        } else {
            setModalData({ uid, status: adjustedStatus, amount: '' });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalData({ uid: '', status: '', amount: '' });
    };

    const handleAmountChange = (e) => {
        setModalData({ ...modalData, amount: e.target.value });
    };

    const handleConfirmUpdate = () => {
        setIsModalOpen(false);
        setIsConfirmModalOpen(true);
    };

    const handleFinalUpdate = async () => {
        try {
            const { uid, status, amount } = modalData;

            if (!amount || isNaN(amount) || amount <= 0) {
                alert('Please enter a valid amount.');
                return;
            }

            if (isBulkAction) {
                await Promise.all(selectedWallets.map(async (wallet) => {
                    const response = await fetch(`${API_URL}/api/wallet/wallet/${wallet.uid}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            amount: +amount,
                            status,
                        }),
                    });

                    const result = await response.json();
                    if (!response.ok) throw new Error(result.message || 'Failed to update wallet');
                }));

                alert('Bulk wallet update successful');
            } else {
                const response = await fetch(`${API_URL}/api/wallet/wallet/${uid}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        amount: +amount,
                        status,
                    }),
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result.message || 'Failed to update wallet');
                alert(result.message);
            }

            setRefresh(prev => !prev);
            closeModal();
            setIsConfirmModalOpen(false);
        } catch (error) {
            console.error('Error updating wallet:', error);
            alert('An unexpected error occurred. Please try again.');
        }
    };

    const handleSelectAll = () => {
        setSelectedWallets(filteredWallets);
    };

    const handleDeselectAll = () => {
        setSelectedWallets([]);
    };

    const handleRowSelected = (state) => {
        setSelectedWallets(state.selectedRows);
    };

    const columns = [
        {
            name: 'Name',
            selector: (row) => (row.username ? row.username : 'N/A'),
            sortable: true,
        },
        {
            name: 'Type',
            selector: (row) => (row.type ? row.type : 'N/A'),
            sortable: true,
        },
        {
            name: 'Phone No.',
            selector: (row) => row.phoneNumber || 'N/A',
            sortable: true,
        },
        {
            name: 'Amount',
            selector: (row) => row.amount,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="flex gap-2">
                    <button
                        className="hover:scale-105 bg-green-500 text-white px-3 py-1 rounded"
                        onClick={() => openModal(row.uid, 'credited')}
                    >
                        Credit
                    </button>
                    <button
                        className="hover:scale-105 bg-red-500 text-white px-3 py-1 rounded"
                        onClick={() => openModal(row.uid, 'debited')}
                    >
                        Debit
                    </button>
                </div>
            ),
        },
    ];

    if (loading) {
        return (
            <div className="flex flex-col h-[98vh] w-full justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-green-500 border-solid"></div>
                <span className="sr-only">Loading...</span>
            </div>
        );
    }

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#1f2937',
                color: '#ffffff',
                fontWeight: 'bold',
                fontSize: '14px',
                padding: '10px 15px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        cells: {
            style: {
                padding: '10px 15px',
                fontSize: '14px',
                color: '#4b5563',
            },
        },
        rows: {
            style: {
                '&:hover': {
                    backgroundColor: '#f3f4f6',
                    cursor: 'pointer',
                },
                borderBottom: '1px solid #e5e7eb',
            },
        },
        pagination: {
            style: {
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontSize: '12px',
            },
            pageButtonsStyle: {
                borderRadius: '5px',
                height: '30px',
                width: '30px',
                padding: '5px',
                margin: '0 5px',
                backgroundColor: '#e5e7eb',
                color: '#374151',
                '&:hover': {
                    backgroundColor: '#1f2937',
                    color: '#ffffff',
                },
            },
        },
    };

    return (
        <>
            <div className="px-8">
                <h1 className="mt-3 mb-4 font-bold text-2xl">Wallets</h1>

                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Search by name, type, phone number or amount..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="border border-gray-300 px-3 py-2 w-full rounded"
                    />
                </div>

                <div className="mb-4 flex gap-4">
                    <button
                        className="hover:scale-105 bg-gray-500 text-white px-4 py-2 rounded"
                        onClick={() => filterWallets('all')}
                    >
                        Show All
                    </button>
                    <button
                        className="hover:scale-105 bg-blue-500 text-white px-4 py-2 rounded"
                        onClick={() => filterWallets('user')}
                    >
                        Users
                    </button>
                    <button
                        className="hover:scale-105 bg-green-500 text-white px-4 py-2 rounded"
                        onClick={() => filterWallets('rider')}
                    >
                        Riders
                    </button>
                    <button
                        className="hover:scale-105 bg-yellow-500 text-white px-4 py-2 rounded"
                        onClick={() => filterWallets('restaurant')}
                    >
                        Restaurants
                    </button>
                </div>

                <div className="mb-4 flex gap-4">
                    <button
                        className="hover:scale-105 bg-purple-500 text-white px-4 py-2 rounded"
                        onClick={handleSelectAll}
                    >
                        Select All
                    </button>
                    <button
                        className="hover:scale-105 bg-red-500 text-white px-4 py-2 rounded"
                        onClick={handleDeselectAll}
                    >
                        Deselect All
                    </button>
                    <button
                        className="hover:scale-105 bg-green-500 text-white px-4 py-2 rounded"
                        onClick={() => openModal('', 'credited', true)}
                        disabled={selectedWallets.length === 0}
                    >
                        Credit Selected
                    </button>
                    <button
                        className="hover:scale-105 bg-red-500 text-white px-4 py-2 rounded"
                        onClick={() => openModal('', 'debited', true)}
                        disabled={selectedWallets.length === 0}
                    >
                        Debit Selected
                    </button>
                </div>

                <div className="shadow-lg bg-white flex flex-col w-full max-w-5xl overflow-y-auto rounded-xl overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={filteredWallets}
                        pagination
                        highlightOnHover
                        pointerOnHover
                        customStyles={customStyles}
                        selectableRows
                        onSelectedRowsChange={handleRowSelected}
                    />
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="hover:scale-105 bg-white rounded-lg p-6 w-96">
                        <h2 className="text-lg font-bold">
                            {modalData.status === 'added' ? 'Credit Wallet' : 'Debit Wallet'}
                        </h2>
                        <p className="text-sm text-gray-500 mb-4">
                            {isBulkAction ?
                                `Enter the amount to ${modalData.status === 'added' ? 'credit' : 'debit'} for selected wallets` :
                                `Enter the amount to ${modalData.status === 'added' ? 'credit' : 'debit'} for user ID: ${modalData.uid}`
                            }
                        </p>
                        <input
                            type="number"
                            placeholder="Enter amount"
                            value={modalData.amount}
                            onChange={handleAmountChange}
                            className="w-full px-3 py-2 border rounded"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                className="hover:scale-105 bg-gray-300 text-black px-3 py-1 rounded"
                                onClick={closeModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="hover:scale-105 bg-blue-500 text-white px-3 py-1 rounded"
                                onClick={handleConfirmUpdate}
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isConfirmModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="hover:scale-105 bg-white rounded-lg p-6 w-96">
                        <h2 className="text-lg font-bold">Confirm Update</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Are you sure you want to {modalData.status === 'added' ? 'credit' : 'debit'} {modalData.amount}?
                        </p>
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                className="hover:scale-105 bg-gray-300 text-black px-3 py-1 rounded"
                                onClick={() => setIsConfirmModalOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="hover:scale-105 bg-green-500 text-white px-3 py-1 rounded"
                                onClick={handleFinalUpdate}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
